import React, { useState, useRef } from 'react';
import { View, ScrollView, Image, Modal, Platform } from 'react-native';
import { CURRENT_USER, dmChannel } from '@ventureroom/core';
import { useApp, useData, useTheme, useMe } from './provider';
import {
  Avatar,
  Button,
  Card,
  Column,
  Copy,
  Empty,
  Input,
  Page,
  Row,
  Tag,
  dateLabel,
  timeLabel,
} from './components';
export function Conversation({
  channel,
  boardroom = false,
}: {
  channel: string;
  boardroom?: boolean;
}) {
  const data = useData();
  const { act, busy } = useApp();
  const t = useTheme();
  const me = useMe();
  const canUpload = (me.role === 'Business Owner' || me.role === 'Both') && (me.subscriptionTier === 'enterprise');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const scrollViewRef = useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fileInputRef = useRef<any>(null);
  const [attachment, setAttachment] = useState<{name: string, url: string, isImage: boolean} | null>(null);
  const [draft, setDraft] = useState(''),
    [reply, setReply] = useState<string | undefined>(),
    [expandedSite, setExpandedSite] = useState<string | undefined>();
  const messages = data.messages
    .filter((m) => m.channel === channel)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const replying = messages.find((m) => m.id === reply);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFileChange = (e: any) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setAttachment({
        name: file.name,
        url: ev.target?.result as string,
        isImage: file.type.startsWith('image/'),
      });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };
  const handleDownload = (websiteCode: { html: string; css: string; js: string }) => {
    const content = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <style>${websiteCode.css}</style>
</head>
<body>
  ${websiteCode.html}
  <script>${websiteCode.js}</script>
</body>
</html>`;
    const blob = new Blob([content], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'venture-website.html';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  async function send() {
    const text = draft;
    if (!text.trim() && !attachment) return;
    setDraft('');
    setReply(undefined);
    const attachUrl = attachment?.url;
    const attachName = attachment?.name;
    const isImage = attachment?.isImage;
    setAttachment(null);
    const success = await act({
      type: 'message',
      channel,
      text: text || (attachName ? `Sent an attachment: ${attachName}` : ''),
      replyTo: reply,
      ...(isImage ? { imageUrl: attachUrl } : {}),
      ...(!isImage && attachUrl ? { fileUrl: attachUrl, fileName: attachName } : {})
    }, 'Message sent');
    if (!success) {
      setDraft(text);
      if (attachUrl && attachName) setAttachment({ name: attachName, url: attachUrl, isImage: isImage ?? false });
    }
  }
  return (
    <Column gap={20} style={{ flex: 1 }}>
      <ScrollView 
        ref={scrollViewRef}
        onContentSizeChange={() => scrollViewRef.current?.scrollToEnd({ animated: false })}
        onLayout={() => scrollViewRef.current?.scrollToEnd({ animated: false })}
        style={{ flex: 1, flexBasis: 0 }} 
        contentContainerStyle={{ paddingBottom: 20, gap: 20, flexGrow: 1, justifyContent: 'flex-end' }}
      >
        {!messages.length && <Empty title="Say hello" detail="Start your conversation." />}
      {messages.map((m) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const person = data.profiles.find((p) => p.id === m.author) || { name: 'VentureBot', avatar: 'sage' as any };
        const own = m.author === CURRENT_USER;
        const parent = messages.find((p) => p.id === m.replyTo);
        return (
          <Row
            key={m.id}
            style={{ alignItems: 'flex-start', flexDirection: own ? 'row-reverse' : 'row' }}
          >
            <Avatar person={person} size={36} />
            <View style={{ maxWidth: '82%', minWidth: 160, gap: 6 }}>
              <Row between wrap>
                <Copy size={12} bold>
                  {person.name.split(' ')[0]}
                </Copy>
                <Copy muted size={10}>
                  {dateLabel(m.createdAt)} · {timeLabel(m.createdAt)}
                </Copy>
              </Row>
              <View
                style={{
                  padding: 16,
                  borderRadius: 18,
                  borderTopRightRadius: own ? 4 : 18,
                  borderTopLeftRadius: own ? 18 : 4,
                  backgroundColor: own ? t.hero : t.surface,
                  borderWidth: 0,
                  borderColor: own ? t.hero : t.border,
                  gap: 8,
                }}
              >
                {parent && (
                  <View style={{ borderLeftWidth: 2, borderColor: t.accent, paddingLeft: 9 }}>
                    <Copy muted size={12}>
                      {parent.text}
                    </Copy>
                  </View>
                )}
                <Copy>{m.text}</Copy>
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                {(() => { const _m = m as any; return (
                  <>
                    {!!_m.imageUrl && <Image source={{ uri: _m.imageUrl }} style={{ width: '100%', height: 200, borderRadius: 8, marginTop: 8 }} resizeMode="cover" />}
                    {!!_m.fileName && !!_m.fileUrl && (
                      <View style={{ marginTop: 8, padding: 12, backgroundColor: t.background, borderRadius: 8 }}>
                        <Copy size={13} bold>📄 {_m.fileName}</Copy>
                      </View>
                    )}
                  </>
                ); })()}
                {!!m.websiteCode && (
                  <>
                    <div style={{ marginTop: 8, borderRadius: 12, overflow: 'hidden', border: '1px solid #ddd', height: 400, width: '100%', position: 'relative' }}>
                      <div style={{ position: 'absolute', top: 12, right: 12, display: 'flex', gap: 8, zIndex: 10 }}>
                        <button onClick={() => handleDownload(m.websiteCode!)} style={{ padding: '6px 12px', background: 'rgba(15, 23, 42, 0.8)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 500 }}>Download Code ↓</button>
                        <button onClick={() => setExpandedSite(m.id)} style={{ padding: '6px 12px', background: 'rgba(15, 23, 42, 0.8)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 500 }}>Expand ↗</button>
                      </div>
                      <iframe
                        srcDoc={`<html><head><style>${m.websiteCode.css}</style></head><body>${m.websiteCode.html}<script>${m.websiteCode.js}</script></body></html>`}
                        style={{ width: '100%', height: '100%', border: 'none' }}
                        sandbox="allow-scripts"
                      />
                    </div>
                    
                    <Modal visible={expandedSite === m.id} transparent={false} animationType="slide">
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100vh', width: '100vw', backgroundColor: '#fff' }}>
                        <div style={{ padding: '12px 16px', backgroundColor: '#f1f5f9', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: 600, color: '#334155' }}>Live Preview</span>
                          <button onClick={() => setExpandedSite(undefined)} style={{ padding: '6px 16px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 500 }}>Close Fullscreen</button>
                        </div>
                        <iframe
                          srcDoc={`<html><head><style>${m.websiteCode.css}</style></head><body>${m.websiteCode.html}<script>${m.websiteCode.js}</script></body></html>`}
                          style={{ width: '100%', flex: 1, border: 'none' }}
                          sandbox="allow-scripts"
                        />
                      </div>
                    </Modal>
                  </>
                )}
                {!!m.imageUrl && (
                  <Image 
                    source={{ uri: m.imageUrl }} 
                    style={{ width: 256, height: 256, borderRadius: 12, marginTop: 8 }} 
                  />
                )}
                {!!m.reference && <Tag>↗ {m.reference}</Tag>}
              </View>
              <Row>
                <Button
                  compact
                  kind="quiet"
                  label={`Reply to ${person.name}`}
                  onPress={() => setReply(m.id)}
                >
                  Reply
                </Button>
                <Button
                  compact
                  kind="quiet"
                  label={`Appreciate message from ${person.name}`}
                  onPress={() => void act({ type: 'react', id: m.id }, 'Reaction updated')}
                >
                  {m.reactions.includes(CURRENT_USER) ? '♥' : '♡'} {m.reactions.length || ''}
                </Button>
              </Row>
            </View>
          </Row>
        );
      })}
      {boardroom &&
        data.activities
          .filter((a) => a.ventureId === channel && a.kind === 'commitment')
          .slice(0, 2)
          .map((a) => (
            <Copy key={a.id} muted size={12}>
              ✓ {a.title}
            </Copy>
          ))}
          {busy && channel.includes('ai-assistant') && (
            <Row style={{ alignItems: 'flex-start', flexDirection: 'row' }}>
              <View style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: '#ddd', overflow: 'hidden' }}>
                <Image source={{ uri: 'https://ui-avatars.com/api/?name=Venture+Bot&background=e0e0e0&color=333' }} style={{ width: 36, height: 36 }} />
              </View>
              <View style={{ backgroundColor: t.border, padding: 16, borderRadius: 12, borderTopLeftRadius: 4, marginLeft: 12, flexDirection: 'row', alignItems: 'center' }}>
                <style>{`
                  @keyframes bounce {
                    0%, 100% { transform: translateY(0); opacity: 0.4; }
                    50% { transform: translateY(-4px); opacity: 1; }
                  }
                  .dot { width: 6px; height: 6px; border-radius: 3px; background-color: #888; margin: 0 2px; animation: bounce 1.4s infinite ease-in-out both; }
                  .dot1 { animation-delay: -0.32s; }
                  .dot2 { animation-delay: -0.16s; }
                  .dot3 { animation-delay: 0s; }
                `}</style>
                <div className="dot dot1" />
                <div className="dot dot2" />
                <div className="dot dot3" />
              </View>
            </Row>
          )}
      </ScrollView>
      <Card>
        {replying && (
          <Row between>
            <View style={{ flex: 1 }}>
              <Copy muted size={12}>
                Replying to: {replying.text}
              </Copy>
            </View>
            <Button compact kind="quiet" onPress={() => setReply(undefined)}>
              Cancel
            </Button>
          </Row>
        )}
        {attachment && (
          <Row between style={{ backgroundColor: t.surfaceAlt, padding: 12, borderRadius: 8, alignItems: 'center' }}>
            <Copy size={13} bold>{attachment.name}</Copy>
            <Button compact kind="quiet" onPress={() => setAttachment(null)}>Remove</Button>
          </Row>
        )}
        <Input
          label={boardroom ? 'Message the boardroom' : 'Your message'}
          value={draft}
          onChange={setDraft}
          onSubmit={() => {
            if (draft.trim() || attachment) void send();
          }}
          multiline
          placeholder="Message…"
        />
        <Row between>
          <Row style={{ gap: 8 }}>
            {(boardroom || channel.startsWith('venturebot-')) && canUpload && Platform.OS === 'web' && (
              <>
                <input
                  type="file"
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  ref={fileInputRef as any}
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
                <Button kind="secondary" compact onPress={() => fileInputRef.current?.click()}>
                  + Attach file
                </Button>
              </>
            )}
          </Row>
          <Button disabled={!draft.trim() && !attachment} onPress={() => void send()}>
            Send ↗
          </Button>
        </Row>
      </Card>
    </Column>
  );
}
export function ChatScreen({ personId }: { personId: string }) {
  const data = useData();
  const { navigate } = useApp();
  const p = data.profiles.find((p) => p.id === personId);
  if (!p)
    return (
      <Page title="Chat unavailable" back="connections">
        <Empty title="Person not found" detail="Open a conversation from Chats." />
      </Page>
    );
  const matched = data.connections.some(
    (c) =>
      (c.status === 'accepted' || (c.status === 'pending' && c.from === CURRENT_USER)) &&
      [c.from, c.to].includes(CURRENT_USER) &&
      [c.from, c.to].includes(personId),
  );
  const shared = data.ventures.filter(
    (v) => v.members.includes(CURRENT_USER) && v.members.includes(personId),
  );
  return (
    <Page
      title={p.name}
      back="connections"
      scroll={false}
      action={
        <Button
          compact
          kind="quiet"
          label={`View ${p.name}'s profile`}
          onPress={() => navigate(`person/${p.id}`)}
        >
          Profile
        </Button>
      }
    >
      {matched ? (
        <>
          {!!shared.length && (
            <Row wrap>
              {shared.map((v) => (
                <Button
                  key={v.id}
                  compact
                  kind="secondary"
                  onPress={() => navigate(`venture/${v.id}`)}
                >
                  {v.name}
                </Button>
              ))}
            </Row>
          )}
          <Conversation channel={dmChannel(CURRENT_USER, personId)} />
        </>
      ) : (
        <Empty
          title="Match to chat"
          detail="You can start a conversation once you have both liked each other."
          action={<Button onPress={() => navigate('discover')}>Discover people</Button>}
        />
      )}
    </Page>
  );
}

export function MeetingRoomScreen({ ownerId }: { ownerId: string }) {
  const data = useData();
  const owner = data.profiles.find((p) => p.id === ownerId);
  
  const hasAccess = owner?.id === CURRENT_USER || owner?.team?.includes(CURRENT_USER);
  if (!owner || !hasAccess) {
    return (
      <Page title="Meeting room" back="connections">
        <Empty title="Room not available" detail="You are not part of this team." />
      </Page>
    );
  }

  return (
    <Page title={`${owner.name}'s Meeting Room`} back="connections" scroll={false}>
      <Conversation channel={`team:${ownerId}`} />
    </Page>
  );
}


